import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RingLoader from 'react-spinners/RingLoader';
import { publishLibraryItem } from '../../services/aiComposer';
import { getUserScreens } from '../../services/getUserScreens';
import { getUserDashboardData } from '../../services/getUserDashboardData';
import { clearContentSlot } from '../../services/contentPlugins';
import { getUserAccessToken } from '../../services/userSession';
import './LibraryItemManageModal.css';

function displaySlotNumber(slotPosition) {
  return slotPosition - 6;
}

function normalizeMediaUrl(url) {
  return url ? url.split('?')[0].toLowerCase() : '';
}

function screenLabel(screen) {
  return screen.screenName?.trim() || screen.address?.trim() || `Screen ${screen.screenId}`;
}

export default function LibraryItemManageModal({
  open,
  item,
  ownerUserId,
  token,
  canManage,
  onClose,
  onChanged,
}) {
  const [step, setStep] = useState('main');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [screens, setScreens] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  const [error, setError] = useState('');

  const authToken = token || getUserAccessToken();
  const itemMediaKey = useMemo(() => normalizeMediaUrl(item?.MediaUrl), [item?.MediaUrl]);

  const loadContext = useCallback(async () => {
    if (!open || !item || !authToken || !ownerUserId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [screensResponse, dashboardResponse] = await Promise.all([
        getUserScreens(authToken, ownerUserId),
        getUserDashboardData(authToken, ownerUserId),
      ]);

      const nextScreens = screensResponse.data?.screens || [];
      setScreens(nextScreens);

      const screenLabelById = new Map(nextScreens.map((screen) => [screen.screenId, screenLabel(screen)]));
      const personalSlots = dashboardResponse.data?.personalContentSlots || [];
      const nextAssignments = [];

      personalSlots.forEach((slot) => {
        const screenId = Number(slot.screenId);
        const slotPosition = Number(slot.slotPosition);
        if (!screenId || !slotPosition) {
          return;
        }

        const liveMatch = normalizeMediaUrl(slot.contentUrl) === itemMediaKey && itemMediaKey;
        const pendingMatch = normalizeMediaUrl(slot.pendingContentUrl) === itemMediaKey && itemMediaKey;
        if (!liveMatch && !pendingMatch) {
          return;
        }

        nextAssignments.push({
          screenId,
          screenLabel: screenLabelById.get(screenId) || `Screen ${screenId}`,
          slotPosition,
          isPending: Boolean(pendingMatch && !liveMatch),
        });
      });

      setAssignments(nextAssignments);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load screens. Try again.');
      setScreens([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, item, itemMediaKey, open, ownerUserId]);

  useEffect(() => {
    if (!open) {
      setStep('main');
      setSelectedScreenId(null);
      setError('');
      return;
    }
    loadContext();
  }, [loadContext, open]);

  const handlePublish = async (slotPosition) => {
    if (!item || !selectedScreenId || !authToken || !ownerUserId) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await publishLibraryItem(authToken, item.ItemId, {
        userId: ownerUserId,
        screenIds: [selectedScreenId],
        slotId: slotPosition,
      });

      if (response.data?.reviewPending) {
        window.alert('Your content was sent for approval before it goes live on the screen.');
      } else {
        window.alert(`Content added to slot ${displaySlotNumber(slotPosition)}.`);
      }

      setStep('main');
      setSelectedScreenId(null);
      await loadContext();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add content to screen.');
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (assignment) => {
    if (!authToken || !ownerUserId) {
      return;
    }

    const confirmed = window.confirm(
      `Remove this content from ${assignment.screenLabel}, slot ${displaySlotNumber(assignment.slotPosition)}?`,
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await clearContentSlot({
        userId: ownerUserId,
        screenId: assignment.screenId,
        slotId: assignment.slotPosition,
      }, authToken);
      await loadContext();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove content from screen.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !item) {
    return null;
  }

  const selectedScreen = screens.find((screen) => screen.screenId === selectedScreenId) || null;
  const slotOptions = [7, 8, 9, 10, 11, 12];

  return (
    <div className="libraryManageOverlay" onClick={saving ? undefined : onClose} role="presentation">
      <div className="libraryManageModal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="libraryManagePreview">
          {item.MediaUrl?.endsWith('.mp4') ? (
            <video src={item.MediaUrl} muted playsInline />
          ) : (
            <img src={item.MediaUrl} alt={item.Title || 'Content'} />
          )}
        </div>

        <h2 className="libraryManageTitle">{item.Title || 'Untitled'}</h2>
        <p className="libraryManageSubtitle">Manage where this content plays on your screens.</p>

        {loading ? (
          <div className="libraryManageLoader">
            <RingLoader color="#dc5024" size={32} />
          </div>
        ) : null}

        {error ? <p className="libraryManageError">{error}</p> : null}

        {step === 'main' ? (
          <div className="libraryManageSection">
            <h3>On screens</h3>
            {assignments.length === 0 ? (
              <p className="libraryManageEmpty">Not assigned to any screens yet.</p>
            ) : (
              assignments.map((assignment) => (
                <div key={`${assignment.screenId}-${assignment.slotPosition}`} className="libraryManageAssignment">
                  <div>
                    <strong>{assignment.screenLabel}</strong>
                    <p>
                      Slot {displaySlotNumber(assignment.slotPosition)}
                      {assignment.isPending ? ' · Pending review' : ''}
                    </p>
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      className="libraryManageRemove"
                      onClick={() => removeAssignment(assignment)}
                      disabled={saving}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))
            )}

            {canManage ? (
              <button
                type="button"
                className="libraryManagePrimary"
                onClick={() => {
                  setError('');
                  if (!screens.length) {
                    setError('No screens found. Link a screen first.');
                    return;
                  }
                  setStep('screen');
                }}
                disabled={saving}
              >
                Add to screen
              </button>
            ) : (
              <p className="libraryManageEmpty">You do not have permission to manage screen content.</p>
            )}
          </div>
        ) : null}

        {step === 'screen' ? (
          <div className="libraryManageSection">
            <h3>Choose a screen</h3>
            {screens.map((screen) => (
              <button
                key={screen.screenId}
                type="button"
                className="libraryManageChoice"
                onClick={() => {
                  setSelectedScreenId(screen.screenId);
                  setStep('slot');
                }}
                disabled={saving}
              >
                <strong>{screenLabel(screen)}</strong>
                {screen.address ? <span>{screen.address}</span> : null}
              </button>
            ))}
            <button type="button" className="libraryManageGhost" onClick={() => setStep('main')}>Back</button>
          </div>
        ) : null}

        {step === 'slot' && selectedScreen ? (
          <div className="libraryManageSection">
            <h3>{screenLabel(selectedScreen)}</h3>
            <p className="libraryManageHint">Pick a personal content slot (1–6).</p>
            <div className="libraryManageSlotGrid">
              {slotOptions.map((slotPosition) => (
                <button
                  key={slotPosition}
                  type="button"
                  className="libraryManageSlotBtn"
                  onClick={() => handlePublish(slotPosition)}
                  disabled={saving}
                >
                  {displaySlotNumber(slotPosition)}
                </button>
              ))}
            </div>
            {saving ? (
              <div className="libraryManageLoader">
                <RingLoader color="#dc5024" size={24} />
              </div>
            ) : null}
            <button
              type="button"
              className="libraryManageGhost"
              onClick={() => {
                setSelectedScreenId(null);
                setStep('screen');
              }}
            >
              Back
            </button>
          </div>
        ) : null}

        <button type="button" className="libraryManageGhost" onClick={onClose} disabled={saving}>Close</button>
      </div>
    </div>
  );
}
